import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../firebase';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import {
  ref, uploadBytesResumable, getDownloadURL, deleteObject,
} from 'firebase/storage';
import {
  FileText, Pin, PinOff, Plus, X, ArrowLeft,
  Sun, Moon, LogOut, Settings, HelpCircle, UserCircle,
  BookOpen, Eye, AlertTriangle, Stethoscope, ClipboardList,
  Upload, Trash2, ExternalLink, Loader2, Pencil,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

// ─── Config sections ──────────────────────────────────────────────────────────

const SECTION_CONFIG = [
  {
    key: 'indication',
    label: 'Indication',
    icon: Stethoscope,
    colorClass: 'text-blue-600 dark:text-blue-400',
    bgClass: 'bg-blue-50 dark:bg-blue-500/10',
    borderClass: 'border-blue-200 dark:border-blue-500/20',
  },
  {
    key: 'materiel',
    label: 'Matériel',
    icon: ClipboardList,
    colorClass: 'text-teal-600 dark:text-teal-400',
    bgClass: 'bg-teal-50 dark:bg-teal-500/10',
    borderClass: 'border-teal-200 dark:border-teal-500/20',
  },
  {
    key: 'procedure',
    label: 'Procédure',
    icon: BookOpen,
    colorClass: 'text-indigo-600 dark:text-indigo-400',
    bgClass: 'bg-indigo-50 dark:bg-indigo-500/10',
    borderClass: 'border-indigo-200 dark:border-indigo-500/20',
  },
  {
    key: 'surveillance',
    label: 'Surveillance',
    icon: Eye,
    colorClass: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-50 dark:bg-amber-500/10',
    borderClass: 'border-amber-200 dark:border-amber-500/20',
  },
  {
    key: 'remarques',
    label: 'Remarques',
    icon: AlertTriangle,
    colorClass: 'text-orange-600 dark:text-orange-400',
    bgClass: 'bg-orange-50 dark:bg-orange-500/10',
    borderClass: 'border-orange-200 dark:border-orange-500/20',
  },
];

const SECTION_KEYS = SECTION_CONFIG.map((s) => s.key);
const SECTION_DISPLAY = Object.fromEntries(SECTION_CONFIG.map((s) => [s.key, s.label]));

const MAX_PDF_SIZE = 10 * 1024 * 1024; // 10 Mo

// ─── Helpers ─────────────────────────────────────────────────────────────────

const validatePdf = (file) => {
  if (!file) return null;
  if (file.size > MAX_PDF_SIZE) return 'Le PDF ne doit pas dépasser 10 Mo';
  return null;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const LoadingGrid = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 animate-pulse"
      >
        <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-3" />
        <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-2" />
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-5" />
        <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded-lg" />
      </div>
    ))}
  </div>
);

const EmptyState = ({ text, sub, onAction, actionLabel }) => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl border border-dashed border-slate-300 dark:border-slate-600 p-10 text-center">
    <FileText className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
    <p className="font-medium text-slate-500 dark:text-slate-400 mb-1">{text}</p>
    <p className="text-sm text-slate-400 dark:text-slate-500 mb-4">{sub}</p>
    {onAction && (
      <button
        onClick={onAction}
        className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-xl transition-colors"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

const ProtocolCard = ({ protocol, isTemplate, onOpen, onTogglePin }) => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-lg dark:hover:shadow-slate-900/50 transition-all duration-200 p-5 flex flex-col">
    <div className="flex items-start justify-between mb-3">
      <span
        className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
          isTemplate
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
            : 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-400'
        }`}
      >
        {isTemplate ? 'Template' : 'Personnel'}
      </span>
      {!isTemplate && onTogglePin && (
        <button
          onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
          title={protocol.pinned ? 'Désépingler' : 'Épingler'}
          className={`p-1.5 rounded-lg transition-colors ${
            protocol.pinned
              ? 'text-amber-500 hover:text-amber-600'
              : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
          }`}
        >
          {protocol.pinned ? <Pin className="w-4 h-4 fill-current" /> : <PinOff className="w-4 h-4" />}
        </button>
      )}
    </div>

    <h3 className="font-bold text-slate-800 dark:text-slate-100 leading-snug mb-1 flex-1">
      {protocol.titre}
    </h3>

    {protocol.categorie && (
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{protocol.categorie}</p>
    )}

    <button
      onClick={onOpen}
      className="mt-auto w-full py-2 bg-slate-50 dark:bg-slate-700 hover:bg-teal-50 dark:hover:bg-teal-500/10 text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 text-sm font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
    >
      <Eye className="w-4 h-4" />
      Ouvrir
    </button>
  </div>
);

// ─── ConfirmDeleteModal ───────────────────────────────────────────────────────

const ConfirmDeleteModal = ({ onConfirm, onCancel, deleting }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
    <div className="absolute inset-0 bg-black/60 dark:bg-black/80" onClick={onCancel} />
    <div className="relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6">
      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/20 mx-auto mb-4">
        <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
      </div>
      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 text-center mb-2">
        Supprimer ce protocole ?
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
        Cette action est irréversible. Le protocole et son PDF associé seront définitivement supprimés.
      </p>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={deleting}
          className="flex-1 py-2.5 px-4 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-colors text-sm font-medium flex items-center justify-center gap-2"
        >
          {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
          {deleting ? 'Suppression…' : 'Supprimer'}
        </button>
      </div>
    </div>
  </div>
);

// ─── ProtocolDetailModal ──────────────────────────────────────────────────────

const ProtocolDetailModal = ({ protocol, uid, onClose, onPdfUpdate, onEdit, onDelete }) => {
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [deletingProtocol, setDeletingProtocol] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const fileInputRef = useRef(null);

  const handleDeleteProtocol = async () => {
    if (!uid || !protocol.id) return;
    setDeletingProtocol(true);
    try {
      if (protocol.pdfUrl) {
        try {
          await deleteObject(ref(storage, `users/${uid}/protocols/${protocol.id}/document.pdf`));
        } catch (e) {
          console.warn('[deleteProtocol] storage:', e.message);
        }
      }
      await deleteDoc(doc(db, 'users', uid, 'protocols', protocol.id));
      toast.success('Protocole supprimé');
      onDelete();
    } catch {
      toast.error('Erreur lors de la suppression');
      setDeletingProtocol(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validatePdf(file);
    if (err) { toast.error(err); return; }
    setPdfFile(file);
  };

  const handleUpload = () => {
    if (!pdfFile || !uid || protocol.isTemplate) return;
    const storageRef = ref(storage, `users/${uid}/protocols/${protocol.id}/document.pdf`);
    const task = uploadBytesResumable(storageRef, pdfFile);
    setUploading(true);
    setUploadProgress(0);
    task.on(
      'state_changed',
      (snap) => {
        setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
      },
      () => {
        toast.error("Erreur lors de l'upload");
        setUploading(false);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        await updateDoc(doc(db, 'users', uid, 'protocols', protocol.id), { pdfUrl: url });
        toast.success('PDF ajouté');
        setUploading(false);
        setPdfFile(null);
        onPdfUpdate(url);
      }
    );
  };

  const handleDeletePdf = async () => {
    if (!uid || !protocol.id) return;
    setDeleting(true);
    try {
      await deleteObject(ref(storage, `users/${uid}/protocols/${protocol.id}/document.pdf`));
    } catch (e) {
      console.warn('[deletePdf] storage:', e.message);
    }
    try {
      await updateDoc(doc(db, 'users', uid, 'protocols', protocol.id), { pdfUrl: null });
      toast.success('PDF supprimé');
      onPdfUpdate(null);
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-2xl bg-white dark:bg-slate-900 h-full overflow-y-auto shadow-2xl flex flex-col">

        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-start justify-between">
          <div className="flex-1 pr-4">
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full mb-2 inline-block ${
                protocol.isTemplate
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                  : 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-400'
              }`}
            >
              {protocol.isTemplate ? 'Template Remadia' : 'Protocole personnel'}
            </span>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mt-1">{protocol.titre}</h2>
            {protocol.categorie && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{protocol.categorie}</p>
            )}
            {!protocol.isTemplate && (
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={onEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 rounded-lg transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Modifier
                </button>
                <button
                  onClick={() => setShowConfirmDelete(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Supprimer
                </button>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {showConfirmDelete && (
          <ConfirmDeleteModal
            onConfirm={handleDeleteProtocol}
            onCancel={() => setShowConfirmDelete(false)}
            deleting={deletingProtocol}
          />
        )}

        {/* Sections */}
        <div className="p-6 space-y-4 flex-1">

          {/* Bandeau disclaimer templates */}
          {protocol.isTemplate && protocol.sourceLabel && (
            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                Ce protocole est basé sur les recommandations officielles de{' '}
                <span className="font-semibold">{protocol.sourceLabel}</span>.
                {' '}Les recommandations évoluent — consultez toujours la source officielle avant utilisation clinique.
              </p>
            </div>
          )}

          {/* Bouton source officielle */}
          {protocol.isTemplate && protocol.sourceUrl && (
            <a
              href={protocol.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 border border-amber-200 dark:border-amber-500/20 rounded-lg transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Consulter la source officielle
            </a>
          )}

          {SECTION_CONFIG.map(({ key, label, icon: Icon, colorClass, bgClass, borderClass }) => {
            const content = protocol.sections?.[key];
            if (!content) return null;
            return (
              <div key={key} className={`p-4 rounded-xl border ${bgClass} ${borderClass}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${colorClass}`} />
                  <h3 className={`text-sm font-bold ${colorClass}`}>{label}</h3>
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-line leading-relaxed">
                  {content}
                </p>
              </div>
            );
          })}

          {/* ── Zone PDF ─────────────────────────────────────────────── */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-5 mt-2">

            {/* PDF existant */}
            {protocol.pdfUrl ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-red-500" />
                    Document PDF
                  </h3>
                  <div className="flex items-center gap-2">
                    <a
                      href={protocol.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 rounded-lg transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Nouvel onglet
                    </a>
                    {!protocol.isTemplate && (
                      <button
                        onClick={handleDeletePdf}
                        disabled={deleting}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
                <iframe
                  src={protocol.pdfUrl}
                  title="Document PDF"
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700"
                  style={{ height: '500px' }}
                />
              </div>
            ) : (
              /* Upload PDF (protocoles personnels uniquement) */
              !protocol.isTemplate && (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">
                    Ajouter un PDF
                  </h3>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={handleFileChange}
                  />

                  {pdfFile ? (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600">
                      <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-200 truncate flex-1">
                        {pdfFile.name}
                      </span>
                      <button
                        onClick={() => { setPdfFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl text-sm text-slate-500 dark:text-slate-400 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Choisir un PDF (max 10 Mo)
                    </button>
                  )}

                  {/* Barre de progression */}
                  {uploading && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                        <span className="flex items-center gap-1.5">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Upload en cours…
                        </span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-teal-500 rounded-full transition-all duration-200"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {pdfFile && !uploading && (
                    <button
                      onClick={handleUpload}
                      className="w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Envoyer le PDF
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── NewProtocolModal ─────────────────────────────────────────────────────────

const NewProtocolModal = ({
  formData, setFormData, onSave, onClose, saving,
  pdfFile, setPdfFile, pdfProgress, title = 'Nouveau protocole',
}) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validatePdf(file);
    if (err) { toast.error(err); return; }
    setPdfFile(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">{title}</h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
              Titre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.titre}
              onChange={(e) => setFormData((prev) => ({ ...prev, titre: e.target.value }))}
              placeholder="Ex : Protocole insuline rapide"
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 text-sm transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
              Catégorie
            </label>
            <input
              type="text"
              value={formData.categorie}
              onChange={(e) => setFormData((prev) => ({ ...prev, categorie: e.target.value }))}
              placeholder="Ex : Abord vasculaire"
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 text-sm transition-colors"
            />
          </div>

          {SECTION_KEYS.map((key) => (
            <div key={key}>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                {SECTION_DISPLAY[key]}
              </label>
              <textarea
                value={formData.sections[key]}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sections: { ...prev.sections, [key]: e.target.value },
                  }))
                }
                placeholder={`Saisir les informations de ${SECTION_DISPLAY[key].toLowerCase()}…`}
                rows={3}
                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 text-sm resize-none transition-colors"
              />
            </div>
          ))}

          {/* PDF optionnel */}
          <div className="pt-1">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
              Document PDF (optionnel, max 10 Mo)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileChange}
            />
            {pdfFile ? (
              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600">
                <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-sm text-slate-700 dark:text-slate-200 truncate flex-1">{pdfFile.name}</span>
                <button
                  onClick={() => { setPdfFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0"
                  disabled={saving}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl text-sm text-slate-500 dark:text-slate-400 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                Ajouter un PDF
              </button>
            )}

            {/* Progression upload PDF (pendant la sauvegarde) */}
            {saving && pdfProgress !== null && (
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Upload PDF…
                  </span>
                  <span>{pdfProgress}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded-full transition-all duration-200"
                    style={{ width: `${pdfProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-2.5 px-4 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="flex-1 py-2.5 px-4 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving
              ? pdfProgress !== null
                ? 'Upload PDF…'
                : 'Enregistrement…'
              : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────

const EMPTY_FORM = {
  titre: '',
  categorie: '',
  sections: {
    indication: '',
    materiel: '',
    procedure: '',
    surveillance: '',
    remarques: '',
  },
};

const ProtocolesInfPage = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [myProtocols, setMyProtocols] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingMy, setLoadingMy] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);

  // États PDF du formulaire de création
  const [formPdfFile, setFormPdfFile] = useState(null);
  const [formPdfProgress, setFormPdfProgress] = useState(null);

  // Fetch templates (tri client pour éviter index composite)
  useEffect(() => {
    const q = query(collection(db, 'protocols'), where('isTemplate', '==', true));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => a.titre.localeCompare(b.titre, 'fr'));
        setTemplates(docs);
        setLoadingTemplates(false);
      },
      (err) => {
        console.error('[templates]', err);
        setLoadingTemplates(false);
      }
    );
    return () => unsub();
  }, []);

  // Fetch protocoles personnels
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'users', user.uid, 'protocols'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setMyProtocols(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingMy(false);
      },
      (err) => {
        console.error('[myProtocols]', err);
        setLoadingMy(false);
      }
    );
    return () => unsub();
  }, [user?.uid]);

  const handleTogglePin = async (protocol) => {
    if (!user?.uid) return;
    try {
      await updateDoc(doc(db, 'users', user.uid, 'protocols', protocol.id), {
        pinned: !protocol.pinned,
      });
      toast.success(protocol.pinned ? 'Protocole désépinglé' : 'Protocole épinglé');
    } catch {
      toast.error("Erreur lors de l'épinglage");
    }
  };

  const handleSaveProtocol = async () => {
    if (!formData.titre.trim()) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      if (editingProtocol) {
        // ── Mode édition : updateDoc ──────────────────────────────────
        await updateDoc(doc(db, 'users', user.uid, 'protocols', editingProtocol.id), {
          titre: formData.titre.trim(),
          categorie: formData.categorie.trim(),
          sections: formData.sections,
          updatedAt: serverTimestamp(),
        });
        toast.success('Protocole mis à jour');
      } else {
        // ── Mode création : addDoc + upload PDF optionnel ─────────────
        const docRef = await addDoc(collection(db, 'users', user.uid, 'protocols'), {
          titre: formData.titre.trim(),
          categorie: formData.categorie.trim(),
          isTemplate: false,
          pinned: false,
          sections: formData.sections,
          createdAt: serverTimestamp(),
        });

        if (formPdfFile) {
          await new Promise((resolve, reject) => {
            const storageRef = ref(storage, `users/${user.uid}/protocols/${docRef.id}/document.pdf`);
            const task = uploadBytesResumable(storageRef, formPdfFile);
            task.on(
              'state_changed',
              (snap) => {
                setFormPdfProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
              },
              reject,
              async () => {
                const url = await getDownloadURL(task.snapshot.ref);
                await updateDoc(docRef, { pdfUrl: url });
                resolve();
              }
            );
          });
        }

        toast.success('Protocole créé');
      }

      setShowForm(false);
      setFormData(EMPTY_FORM);
      setFormPdfFile(null);
      setEditingProtocol(null);
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setFormPdfProgress(null);
    }
  };

  const handleOpenEdit = (protocol) => {
    setFormData({
      titre: protocol.titre || '',
      categorie: protocol.categorie || '',
      sections: {
        indication: protocol.sections?.indication || '',
        materiel: protocol.sections?.materiel || '',
        procedure: protocol.sections?.procedure || '',
        surveillance: protocol.sections?.surveillance || '',
        remarques: protocol.sections?.remarques || '',
      },
    });
    setEditingProtocol(protocol);
    setSelectedProtocol(null);
    setShowForm(true);
  };

  // Mise à jour locale de selectedProtocol après upload/suppression PDF
  const handlePdfUpdate = (url) => {
    setSelectedProtocol((prev) => prev ? { ...prev, pdfUrl: url } : prev);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Déconnexion réussie');
      navigate('/auth/pro');
    } catch {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const lastName = user?.last_name || '';
  const prefix = user?.profile_type === 'medecin' ? 'Dr.' : 'IDE.';
  const displayName = `${prefix} ${lastName}`;
  const initials =
    user?.first_name && user?.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
      : 'ID';

  const sortedMyProtocols = [...myProtocols].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return 0;
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 transition-colors duration-200">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-sm transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/pro/infirmier')}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img
              src="/RemaH.png"
              alt="Remadia Pro"
              className="w-10 h-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-xl font-bold text-[#003D82] dark:text-blue-400">Remadia</span>
            <span className="px-2 py-1 bg-teal-500/10 text-teal-600 dark:text-teal-400 text-xs font-semibold rounded-full border border-teal-500/20 dark:border-teal-500/30">
              PRO
            </span>
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
              aria-label="Changer de thème"
            >
              {isDark ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200">
                  <div className="w-8 h-8 rounded-full bg-[#003D82] dark:bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                    {initials}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {displayName}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100">
                <DropdownMenuItem onClick={() => navigate('/pro/infirmier/profil')} className="dark:hover:bg-slate-700">
                  <UserCircle className="w-4 h-4 mr-2" />Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/pro/infirmier/compte')} className="dark:hover:bg-slate-700">
                  <Settings className="w-4 h-4 mr-2" />Gestion du compte
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/pro/infirmier/faq')} className="dark:hover:bg-slate-700">
                  <HelpCircle className="w-4 h-4 mr-2" />Aide / FAQ
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-slate-700" />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive dark:text-red-400 dark:hover:bg-slate-700">
                  <LogOut className="w-4 h-4 mr-2" />Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-r from-[#003D82] to-[#0056B3] dark:from-slate-800 dark:to-teal-900 text-white py-10 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <button
              onClick={() => navigate('/pro/infirmier')}
              className="flex items-center gap-1 text-blue-200 hover:text-white transition-colors text-sm mb-3"
            >
              <ArrowLeft className="w-4 h-4" />
              Tableau de bord
            </button>
            <h1 className="text-3xl font-bold mb-1">Protocoles de soins</h1>
            <p className="text-blue-100 dark:text-blue-200">
              Vos protocoles personnels et les templates Remadia
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#003D82] font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-md self-start sm:self-auto flex-shrink-0"
          >
            <Plus className="w-5 h-5" />
            Nouveau protocole
          </button>
        </div>
      </section>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-10">

        {/* Mes protocoles */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-lg bg-teal-500/10 dark:bg-teal-500/20 flex items-center justify-center">
              <FileText className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Mes protocoles</h2>
            <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs rounded-full">
              {myProtocols.length}
            </span>
          </div>

          {loadingMy ? (
            <LoadingGrid />
          ) : sortedMyProtocols.length === 0 ? (
            <EmptyState
              text="Aucun protocole personnel"
              sub="Créez votre premier protocole personnalisé"
              onAction={() => setShowForm(true)}
              actionLabel="Créer un protocole"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedMyProtocols.map((p) => (
                <ProtocolCard
                  key={p.id}
                  protocol={p}
                  isTemplate={false}
                  onOpen={() => setSelectedProtocol(p)}
                  onTogglePin={() => handleTogglePin(p)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Templates Remadia */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Templates Remadia</h2>
            <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs rounded-full">
              {templates.length}
            </span>
          </div>

          {loadingTemplates ? (
            <LoadingGrid />
          ) : templates.length === 0 ? (
            <EmptyState
              text="Aucun template disponible"
              sub="Les templates Remadia seront bientôt disponibles"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((p) => (
                <ProtocolCard
                  key={p.id}
                  protocol={p}
                  isTemplate={true}
                  onOpen={() => setSelectedProtocol(p)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {selectedProtocol && (
        <ProtocolDetailModal
          protocol={selectedProtocol}
          uid={user?.uid}
          onClose={() => setSelectedProtocol(null)}
          onPdfUpdate={handlePdfUpdate}
          onEdit={() => handleOpenEdit(selectedProtocol)}
          onDelete={() => setSelectedProtocol(null)}
        />
      )}

      {showForm && (
        <NewProtocolModal
          formData={formData}
          setFormData={setFormData}
          onSave={handleSaveProtocol}
          onClose={() => {
            if (!saving) {
              setShowForm(false);
              setFormData(EMPTY_FORM);
              setFormPdfFile(null);
              setEditingProtocol(null);
            }
          }}
          saving={saving}
          pdfFile={formPdfFile}
          setPdfFile={setFormPdfFile}
          pdfProgress={formPdfProgress}
          title={editingProtocol ? 'Modifier le protocole' : 'Nouveau protocole'}
        />
      )}
    </div>
  );
};

export default ProtocolesInfPage;
